import React, { useEffect, useRef, useState } from "react";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import CloseIcon from "@mui/icons-material/Close";
import "./Gallery.css";

/* Import images dynamically */
function importAll(r) {
  return r.keys().map(r);
}

const images = importAll(
  require.context("../assets/2024/images", false, /\.(png|jpe?g|webp)$/)
);

function Gallery() {
  const [index, setIndex] = useState(0);
  const [lightbox, setLightbox] = useState(null);
  const [slidesPerView, setSlidesPerView] = useState(1);
  const trackRef = useRef(null);

  const startX = useRef(0);
  const isDragging = useRef(false);

  /* -----------------------------
     Responsive slides per view
  ----------------------------- */
  useEffect(() => {
    const updateSlides = () => {
      const w = window.innerWidth;
      if (w < 480) setSlidesPerView(1);
      else if (w < 768) setSlidesPerView(2);
      else if (w < 1024) setSlidesPerView(3);
      else setSlidesPerView(4);
    };

    updateSlides();
    window.addEventListener("resize", updateSlides);
    return () => window.removeEventListener("resize", updateSlides);
  }, []);

  /* -----------------------------
     Auto play (mobile safe)
  ----------------------------- */
  useEffect(() => {
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % images.length);
    }, 4500);

    return () => clearInterval(timer);
  }, []);

  /* -----------------------------
     Swipe handlers
  ----------------------------- */
  const onStart = (x) => {
    isDragging.current = true;
    startX.current = x;
  };

  const onEnd = (x) => {
    if (!isDragging.current) return;
    isDragging.current = false;

    const diff = x - startX.current;
    if (diff > 50) prev();
    if (diff < -50) next();
  };

  /* -----------------------------
     Navigation
  ----------------------------- */
  const next = () =>
    setIndex((prev) => (prev + 1) % images.length);

  const prev = () =>
    setIndex((prev) => (prev - 1 + images.length) % images.length);

  /* -----------------------------
     Render slides
  ----------------------------- */
  const visibleSlides = [];
  for (let i = 0; i < slidesPerView + 2; i++) {
    visibleSlides.push(images[(index + i) % images.length]);
  }

  return React.createElement(
    "section",
    { className: "gallery-section", id: "gallery" },

    React.createElement("h2", { className: "gallery-title" }, "Gallery"),

    React.createElement(
      "div",
      { className: "carousel" },

      React.createElement(
        "button",
        { className: "nav-btn left", onClick: prev },
        React.createElement(ArrowBackIosNewIcon)
      ),

      React.createElement(
        "div",
        {
          className: "carousel-window",
          onTouchStart: (e) => onStart(e.touches[0].clientX),
          onTouchEnd: (e) => onEnd(e.changedTouches[0].clientX),
          onMouseDown: (e) => onStart(e.clientX),
          onMouseUp: (e) => onEnd(e.clientX)
        },

        React.createElement(
          "div",
          {
            className: "carousel-track",
            ref: trackRef,
            style: {
              transform: "translateX(0)",
            }
          },

          visibleSlides.map((img, i) =>
            React.createElement(
              "div",
              {
                key: i,
                className: "slide",
                onClick: () => setLightbox(img)
              },
              React.createElement("img", {
                src: img,
                alt: "Gallery item"
              })
            )
          )
        )
      ),

      React.createElement(
        "button",
        { className: "nav-btn right", onClick: next },
        React.createElement(ArrowForwardIosIcon)
      )
    ),

    /* Dots */
    React.createElement(
      "div",
      { className: "dots" },
      images.map((_, i) =>
        React.createElement("span", {
          key: i,
          className: i === index ? "dot active" : "dot",
          onClick: () => setIndex(i)
        })
      )
    ),

    /* Lightbox */
    lightbox &&
      React.createElement(
        "div",
        { className: "lightbox", onClick: () => setLightbox(null) },

        React.createElement(
          "button",
          { className: "close-btn" },
          React.createElement(CloseIcon)
        ),

        React.createElement("img", {
          src: lightbox,
          alt: "Preview"
        })
      )
  );
}

export default Gallery;
